import {TableTheme} from '@/constants/table';
import {SnakeCase} from '@/types/utils';
import {Condition} from './condition';
import {Event} from './event';
import {ArrayFilter, ConditionFilter} from './filter';
import {GeneratedList, GeneratedValue, SupportsSwitch, TemplateValue} from './instructions';
import {LocalizedString} from './localized';
import {PluginValue} from './plugin';
import {Reference} from './reference';
import {Validation} from './validation';

export type FieldLayoutOptionsVariant = 'primary' | 'success' | 'error' | 'warning';
export type FieldLayoutOptionsSize = 'small' | 'base';

export type FieldLayoutOptions = {
  colSpan?: number;
  size?: FieldLayoutOptionsSize;
  variant?: FieldLayoutOptionsVariant;
};

type BaseFields = {
  id: string;
  layout?: FieldLayoutOptions;
  label: SupportsSwitch<TemplateValue | LocalizedString>;
  helper?: SupportsSwitch<TemplateValue | LocalizedString>;
  displayIf?: Condition[];
  onMount?: Event[];
};

export type InputFields = BaseFields & {
  maxLength?: SupportsSwitch<number | Reference>;
  maxValue?: number;
  minValue?: number;
  placeholder?: LocalizedString;
  initialValue?: SupportsSwitch<Reference | TemplateValue | GeneratedList<ResolvableValue> | string>;
  value?: SupportsSwitch<Reference | TemplateValue | GeneratedValue | string>;
  validations?: SupportsSwitch<Validation[]>;
  readonly?: boolean;
  readonlyIf?: boolean | Condition[];
  disabled?: boolean;
  disabledIf?: boolean | Condition[];
  required?: boolean;
  requiredIf?: boolean | Condition[];
  onChange?: Event[];
  onFocus?: Event[];
  onBlur?: Event[];
  onClick?: Event[];
};

export type TableActionButton = {
  label: LocalizedString;
  theme?: 'primary' | 'secondary' | 'tertiary';
  trackingKey: string;
};

export type TableFields = Omit<BaseFields, 'label'> & {
  table: {
    array?: string;
    staticRowCount?: number;
    theme?: TableTheme;
    columns: {
      header: SupportsSwitch<LocalizedString>;
      value: SupportsSwitch<Reference | TemplateValue | LocalizedString | string>;
    }[];
    onRowClick?: Event[];
    filters?: (ArrayFilter | ConditionFilter)[];
    disableRowIf?: Condition[];
    actionButton?: SupportsSwitch<TableActionButton>;
  };
};

type MaskedField = {
  uppercasedMask?: boolean;
  mask?: SupportsSwitch<string>;
  raw?: boolean;
};

export type ResolvableValue = string | TemplateValue | LocalizedString | Reference;

export type Option = {
  label: ResolvableValue;
  value: ResolvableValue;
};

type ChoiceField = {
  options: SupportsSwitch<Reference | GeneratedList<ResolvableValue | Option> | PluginValue>;
};

type MultipleChoiceField = ChoiceField & {
  multiple?: boolean;
};

type CheckboxFields = Partial<MultipleChoiceField>;

type FilesField = {
  metadata?: Record<string, string>;
};

export type ButtonProperties = {
  label: LocalizedString;
  icon?: IconType;
};

type ButtonFields = {
  button: ButtonProperties;
  onClick: Event[];
};

type HiddenFields = {
  format?: InputFieldType;
};

export const ICON_TYPES: Record<string, IconType> = {
  ADD: 'add',
} as const;

export type IconType = 'add';

export const UI_FIELD_TYPES = {
  MESSAGE: 'message',
  SUB_SECTION_TITLE: 'subSectionTitle',
  TABLE: 'table',
  SPACER: 'spacer',
  DIVIDER: 'divider',
} satisfies Record<Uppercase<SnakeCase<UIFieldType>>, UIFieldType>;

export const INPUT_FIELD_TYPES = {
  BUTTON: 'button',
  CHECKBOX: 'checkbox',
  COMBOBOX: 'combobox',
  CURRENCY: 'currency',
  DATE: 'date',
  EMAIL: 'email',
  FILE: 'file',
  HIDDEN: 'hidden',
  NUMBER: 'number',
  PERCENTAGE: 'percentage',
  RADIO: 'radio',
  SELECT: 'select',
  TEL: 'tel',
  TEXT: 'text',
} satisfies Record<Uppercase<SnakeCase<InputFieldType>>, InputFieldType>;

export const FIELD_TYPES = {
  ...INPUT_FIELD_TYPES,
  ...UI_FIELD_TYPES,
} satisfies Record<Uppercase<SnakeCase<FieldType>>, FieldType>;

export type Field =
  | ({type: 'text'} & InputFields & MaskedField)
  | ({type: 'number'} & InputFields & MaskedField)
  | ({type: 'email'} & InputFields & MaskedField)
  | ({type: 'tel'} & InputFields & MaskedField)
  | ({type: 'date'} & InputFields & MaskedField)
  | ({type: 'currency'} & InputFields)
  | ({type: 'percentage'} & InputFields)
  | ({type: 'select'} & InputFields & MaskedField & MultipleChoiceField)
  | ({type: 'combobox'} & InputFields & MultipleChoiceField)
  | ({type: 'radio'} & InputFields & MaskedField & ChoiceField)
  | ({type: 'checkbox'} & InputFields & MaskedField & CheckboxFields)
  | ({type: 'file'} & InputFields & FilesField)
  | ({type: 'button'} & InputFields & ButtonFields)
  | ({type: 'hidden'} & InputFields & HiddenFields)
  | ({type: 'message'} & BaseFields)
  | ({type: 'spacer'} & Omit<BaseFields, 'label'>)
  | ({type: 'divider'} & Omit<BaseFields, 'label'>)
  | ({type: 'subSectionTitle'} & BaseFields)
  | ({type: 'table'} & TableFields);

export type FieldType = Field['type'];

export const FIELD_CATEGORIES = {
  INPUT: 'input',
  UI: 'ui',
} satisfies Record<string, FieldCategoryType>;

export type FieldCategoryType = 'input' | 'ui';

export const InputFieldTypes = [
  'text',
  'number',
  'email',
  'tel',
  'date',
  'select',
  'combobox',
  'radio',
  'checkbox',
  'file',
  'currency',
  'percentage',
  'button',
  'hidden',
] as const;

export const ChoiceFieldTypes = ['select', 'combobox', 'radio', 'checkbox'] as const;

export type ChoiceFieldType = (typeof ChoiceFieldTypes)[number];

export type InputFieldType = (typeof InputFieldTypes)[number];
export type InputField = Extract<Field, {type: InputFieldType}>;

export const UIFieldTypes = ['message', 'subSectionTitle', 'table', 'spacer', 'divider'] as const;

export type UIFieldType = (typeof UIFieldTypes)[number];
export type UIField = Extract<Field, {type: UIFieldType}>;
